import type { ReactNode } from "react";
import { FieldArrayWithId, FieldErrors, UseFormRegister, UseFormSetValue } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { CodedInput } from "@/modules/add-new-data/components/coded-input";
import { ScrollableFormShell } from "@/modules/add-new-data/components/scrollable-form-shell";
import { resolveMemberAge } from "@/modules/add-new-data/lib/age";
import { getApplicabilityForQuestion, notApplicableValue, type MemberFieldName } from "@/modules/add-new-data/lib/cleansing";
import { AddNewDataFormValues } from "@/modules/add-new-data/lib/schema";
import { cleanRBIMCode, type RbimCodeQuestionKey } from "@/lib/rbimCodeMap";
import { cn } from "@/lib/utils";

type RbimEconomicHealthPagesStepProps = {
  errors: FieldErrors<AddNewDataFormValues>;
  fields: FieldArrayWithId<AddNewDataFormValues, "members", "id">[];
  watchedMembers: AddNewDataFormValues["members"];
  register: UseFormRegister<AddNewDataFormValues>;
  setValue: UseFormSetValue<AddNewDataFormValues>;
};

const cellInput =
  "h-8 w-full min-w-0 rounded-none border-0 bg-white px-1.5 text-xs shadow-none focus-visible:ring-1 focus-visible:ring-emerald-700 focus-visible:ring-offset-0";

export function RbimEconomicHealthPagesStep({
  errors,
  fields,
  watchedMembers,
  register,
  setValue,
}: RbimEconomicHealthPagesStepProps) {
  return (
    <div className="space-y-5">
      <div className="rounded-xl border bg-white p-4 shadow-sm">
        <p className="text-sm font-semibold text-slate-950">Pages 4 - 5: Economic Activity and Health Information</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Continue encoding member-level RBIM questions Q16 to Q26. Scroll sideways to complete the page tables.
        </p>
      </div>

      <ScrollableFormShell minWidthClassName="min-w-[3190px]">
        <table className="w-full table-fixed border-collapse text-center text-xs text-slate-950">
          <colgroup>
            <col className="w-[72px]" />
            <col className="w-[310px]" />
            <col className="w-[110px]" />
            <col className="w-[100px]" />
            <col className="w-[200px]" />
            <col className="w-[200px]" />
            <col className="w-[200px]" />
            <col className="w-[200px]" />
            <col className="w-[200px]" />
            <col className="w-[200px]" />
            <col className="w-[200px]" />
            <col className="w-[200px]" />
            <col className="w-[200px]" />
            <col className="w-[200px]" />
            <col className="w-[200px]" />
          </colgroup>
          <thead>
            <tr>
              <th rowSpan={4} className="border border-black bg-[#eeeeee] px-2 py-2 align-bottom font-medium">
                LINE<br />LIST<br />NO.
              </th>
              <th colSpan={3} className="border border-black bg-[#eeeeee] px-2 py-2 text-sm font-bold uppercase">
                A. Demographic Characteristics
              </th>
              <th colSpan={4} className="border border-black bg-[#eeeeee] px-2 py-2 text-sm font-bold uppercase">
                B. Economic Activity
              </th>
              <th colSpan={7} className="border border-black bg-[#eeeeee] px-2 py-2 text-sm font-bold uppercase">
                C. Health Information
              </th>
            </tr>
            <tr>
              <th colSpan={3} className="border border-black bg-[#eeeeee] px-2 py-2 font-medium uppercase">
                For All Household Members
              </th>
              <th colSpan={4} className="border border-black bg-[#eeeeee] px-2 py-2 font-medium uppercase">
                For 15 Years Old and Above
              </th>
              <th colSpan={3} className="border border-black bg-[#eeeeee] px-2 py-2 font-medium uppercase">
                For 0 to 11 Months Old
              </th>
              <th colSpan={4} className="border border-black bg-[#eeeeee] px-2 py-2 font-medium uppercase">
                For Women 10 to 54 Years Old
              </th>
            </tr>
            <tr>
              <QuestionHeader code="Q1" title="Name" />
              <QuestionHeader code="Q3" title="Sex" />
              <QuestionHeader code="Q4" title="Age" />
              <QuestionHeader code="Q16" title="Monthly Income" />
              <QuestionHeader code="Q17" title="Source of Income" />
              <QuestionHeader code="Q18" title="Status of Work/Business" />
              <QuestionHeader code="Q19" title="Place of Work/Business" />
              <QuestionHeader code="Q20" title="Place of Delivery" />
              <QuestionHeader code="Q21" title="Birth Attendant" />
              <QuestionHeader code="Q22" title="Immunization" />
              <QuestionHeader code="Q23" title="Living Children" />
              <QuestionHeader code="Q24" title="Family Planning (FP) Use" />
              <QuestionHeader code="Q25" title="Source of FP Method" />
              <QuestionHeader code="Q26" title="Intention to Use FP" />
            </tr>
            <tr>
              <Instruction>Copy from previous response</Instruction>
              <Instruction />
              <Instruction />
              <Instruction>How much is ___'s monthly income in PHP?</Instruction>
              <Instruction>What is the major source of ___'s income?</Instruction>
              <Instruction>What is the status of ___'s work/business?</Instruction>
              <Instruction>In what barangay and city/municipality is ___'s work/business located?</Instruction>
              <Instruction>Where was ___ delivered?</Instruction>
              <Instruction>Who attended in the delivery of ___?</Instruction>
              <Instruction>What is the last vaccine received by ___?</Instruction>
              <Instruction>How many pregnancies does ___ had? How many child/ren are still living?</Instruction>
              <Instruction>What FP method are _____ and his/her partner currently using?</Instruction>
              <Instruction>If using FP, where did they obtain the FP method?</Instruction>
              <Instruction>Does ___ and partner intend to use FP method? If yes, what method? If no, why not?</Instruction>
            </tr>
          </thead>
          <tbody>
            {fields.map((field, index) => {
              const member = watchedMembers[index] ?? (field as AddNewDataFormValues["members"][number]);
              const memberErrors = errors.members?.[index];
              const age = resolveMemberAge(member ?? {}).ageDisplay || null;
              const name = [member?.lastName, member?.firstName, member?.middleName].filter(Boolean).join(", ");
              const q16Skipped = isSkipped(member, "monthlyIncome");
              const q17Skipped = isSkipped(member, "sourceOfIncome");
              const q18Skipped = isSkipped(member, "workStatus");
              const q19Skipped = isSkipped(member, "placeOfWork");
              const q20Skipped = isSkipped(member, "placeOfDelivery");
              const q21Skipped = isSkipped(member, "birthAttendant");
              const q22Skipped = isSkipped(member, "immunization");
              const q23Skipped = isSkipped(member, "livingChildren");
              const q24Skipped = isSkipped(member, "familyPlanningUse");
              const q25Skipped = isSkipped(member, "fpMethodSource");
              const q26Skipped = isSkipped(member, "intentionToUseFp");
              const q17NeedsSpecify = ["1", "2", "3", "4", "5"].some((code) =>
                shouldShowDetail(member?.sourceOfIncome, "q17_source_of_income", code),
              );
              const q20Other = shouldShowDetail(member?.placeOfDelivery, "q20_place_of_delivery", "5");
              const q21Other = shouldShowDetail(member?.birthAttendant, "q21_birth_attendant", "5");
              const q23TotalIsZero = isZeroEntry(member?.livingChildren);
              const q25Other = shouldShowDetail(member?.fpMethodSource, "q25_fp_method_source", "6");
              const q26Detail = shouldShowFpIntentionDetail(member?.intentionToUseFp);

              const q16IncomeIsZero = isZeroEntry(member?.monthlyIncome);
              const q16Registration = register(`members.${index}.monthlyIncome`, {
                onChange: (event) => {
                  const rawIncome = String(event.target?.value ?? "")
                    .trim()
                    .replace(/,/g, "");

                  const isNumericIncome = /^\d+(?:\.\d+)?$/.test(rawIncome);
                  const nextIncome = isNumericIncome ? Number(rawIncome) : null;

                  const autoOptions = {
                    shouldDirty: true,
                    shouldTouch: false,
                    shouldValidate: true,
                  } as const;

                  if (rawIncome !== "" && nextIncome === 0) {
                    setValue(`members.${index}.sourceOfIncome`, "99 - N/A", autoOptions);
                    setValue(`members.${index}.sourceOfIncomeOther`, "", autoOptions);
                    setValue(`members.${index}.workStatus`, "99 - N/A", autoOptions);
                    setValue(`members.${index}.placeOfWork`, "99 - N/A", autoOptions);
                    return;
                  }

                  if (nextIncome !== null && nextIncome > 0) {
                    if (String(member?.sourceOfIncome ?? "").trim() === "99 - N/A") {
                      setValue(`members.${index}.sourceOfIncome`, "", autoOptions);
                      setValue(`members.${index}.sourceOfIncomeOther`, "", autoOptions);
                    }

                    if (String(member?.workStatus ?? "").trim() === "99 - N/A") {
                      setValue(`members.${index}.workStatus`, "", autoOptions);
                    }

                    if (String(member?.placeOfWork ?? "").trim() === "99 - N/A") {
                      setValue(`members.${index}.placeOfWork`, "", autoOptions);
                    }
                  }
                },
              });

              const q17Cleaned = cleanRBIMCode(
                "q17_source_of_income",
                member?.sourceOfIncome,
              );
              const q17Code = q17Cleaned.error
                ? ""
                : String(q17Cleaned.code ?? "");
              const q17ForcesWorkNa = ["3", "4", "5"].includes(q17Code);

              const q17Registration = register(`members.${index}.sourceOfIncome`, {
                onChange: (event) => {
                  const nextQ17 = cleanRBIMCode(
                    "q17_source_of_income",
                    event.target?.value,
                  );

                  const nextCode = nextQ17.error
                    ? ""
                    : String(nextQ17.code ?? "");

                  const autoOptions = {
                    shouldDirty: true,
                    shouldTouch: false,
                    shouldValidate: true,
                  } as const;

                  if (["3", "4", "5"].includes(nextCode)) {
                    setValue(
                      `members.${index}.workStatus`,
                      "99 - N/A",
                      autoOptions,
                    );

                    setValue(
                      `members.${index}.placeOfWork`,
                      "99 - N/A",
                      autoOptions,
                    );

                    return;
                  }

                  if (["1", "2"].includes(nextCode)) {
                    if (
                      String(member?.workStatus ?? "").trim() === "99 - N/A"
                    ) {
                      setValue(
                        `members.${index}.workStatus`,
                        "",
                        autoOptions,
                      );
                    }

                    if (
                      String(member?.placeOfWork ?? "").trim() === "99 - N/A"
                    ) {
                      setValue(
                        `members.${index}.placeOfWork`,
                        "",
                        autoOptions,
                      );
                    }
                  }
                },
              });

              const q26Registration = register(`members.${index}.intentionToUseFp`, {
                onChange: (event) => {
                  const nextBranch = shouldShowFpIntentionDetail(event.target?.value);

                  if (nextBranch === q26Detail) {
                    return;
                  }

                  setValue(`members.${index}.intentionToUseFpDetail`, "", {
                    shouldDirty: true,
                    shouldTouch: false,
                    shouldValidate: false,
                  });
                },
              });

              return (
                <tr key={field.id} className="h-[48px]">
                  <LineCell>{index + 1}</LineCell>
                  <td className="border border-black bg-white px-2 py-1 text-left align-middle text-xs font-medium">
                    {name || "Copy from Q1"}
                  </td>
                  <td className="border border-black bg-white px-2 py-1 align-middle">{member?.sex || ""}</td>
                  <td className="border border-black bg-white px-2 py-1 align-middle">{age === null ? "" : age}</td>
                  <DataCell error={memberErrors?.monthlyIncome?.message}>
                    {q16Skipped ? (
                      <SkippedInput ariaLabel={`Line ${index + 1} Q16 not applicable`} />
                    ) : (
                      <MonthlyIncomeInput registration={q16Registration} />
                    )}
                  </DataCell>
                  <DataCell error={memberErrors?.sourceOfIncome?.message ?? memberErrors?.sourceOfIncomeOther?.message}>
                    {q17Skipped || q16IncomeIsZero ? (
                      <SkippedInput ariaLabel={`Line ${index + 1} Q17 not applicable`} />
                    ) : (
                      <>
                        <CodedInput
                          className={cellInput}
                          data-grid-input="true"
                          inputMode="numeric"
                          placeholder="1"
                          questionKey="q17_source_of_income"
                          registration={q17Registration}
                          value={member?.sourceOfIncome}
                        />
                        {q17NeedsSpecify ? (
                          <Input
                            autoUppercase
                            className={cn(cellInput, "border-t border-slate-300", memberErrors?.sourceOfIncomeOther?.message && "bg-red-50")}
                            data-grid-input="true"
                            placeholder="Specify"
                            {...register(`members.${index}.sourceOfIncomeOther`)}
                          />
                        ) : null}
                      </>
                    )}
                  </DataCell>
                  <DataCell error={memberErrors?.workStatus?.message}>
                    {q18Skipped || q16IncomeIsZero || q17ForcesWorkNa ? (
                      <SkippedInput ariaLabel={`Line ${index + 1} Q18 not applicable`} />
                    ) : (
                      <CodedInput
                        className={cellInput}
                        data-grid-input="true"
                        inputMode="numeric"
                        placeholder="1"
                        questionKey="q18_work_status"
                        registration={register(`members.${index}.workStatus`)}
                        value={member?.workStatus}
                      />
                    )}
                  </DataCell>
                  <DataCell error={memberErrors?.placeOfWork?.message}>
                    {q19Skipped || q16IncomeIsZero || q17ForcesWorkNa ? (
                      <SkippedInput ariaLabel={`Line ${index + 1} Q19 not applicable`} />
                    ) : (
                      <Input
                        autoUppercase
                        className={cellInput}
                        data-grid-input="true"
                        placeholder="Barangay, City/Municipality"
                        {...register(`members.${index}.placeOfWork`)}
                      />
                    )}
                  </DataCell>
                  <DataCell error={memberErrors?.placeOfDelivery?.message ?? memberErrors?.placeOfDeliveryOther?.message}>
                    {q20Skipped ? (
                      <SkippedInput ariaLabel={`Line ${index + 1} Q20 not applicable`} />
                    ) : (
                      <>
                        <CodedInput
                          className={cellInput}
                          data-grid-input="true"
                          inputMode="numeric"
                          placeholder="1"
                          questionKey="q20_place_of_delivery"
                          registration={register(`members.${index}.placeOfDelivery`)}
                          value={member?.placeOfDelivery}
                        />
                        {q20Other ? (
                          <Input
                            autoUppercase
                            className={cn(cellInput, "border-t border-slate-300", memberErrors?.placeOfDeliveryOther?.message && "bg-red-50")}
                            data-grid-input="true"
                            placeholder="Specify"
                            {...register(`members.${index}.placeOfDeliveryOther`)}
                          />
                        ) : null}
                      </>
                    )}
                  </DataCell>
                  <DataCell error={memberErrors?.birthAttendant?.message ?? memberErrors?.birthAttendantOther?.message}>
                    {q21Skipped ? (
                      <SkippedInput ariaLabel={`Line ${index + 1} Q21 not applicable`} />
                    ) : (
                      <>
                        <CodedInput
                          className={cellInput}
                          data-grid-input="true"
                          inputMode="numeric"
                          placeholder="1"
                          questionKey="q21_birth_attendant"
                          registration={register(`members.${index}.birthAttendant`)}
                          value={member?.birthAttendant}
                        />
                        {q21Other ? (
                          <Input
                            autoUppercase
                            className={cn(cellInput, "border-t border-slate-300", memberErrors?.birthAttendantOther?.message && "bg-red-50")}
                            data-grid-input="true"
                            placeholder="Specify"
                            {...register(`members.${index}.birthAttendantOther`)}
                          />
                        ) : null}
                      </>
                    )}
                  </DataCell>
                  <DataCell error={memberErrors?.immunization?.message}>
                    {q22Skipped ? (
                      <SkippedInput ariaLabel={`Line ${index + 1} Q22 not applicable`} />
                    ) : (
                      <Input
      className={cellInput}
      data-grid-input="true"
      placeholder="Enter vaccine received"
      {...register(`members.${index}.immunization`)}
    />
                    )}
                  </DataCell>
                  <DataCell error={memberErrors?.livingChildren?.message ?? memberErrors?.livingChildrenAlive?.message}>
                    {q23Skipped ? (
                      <SkippedInput ariaLabel={`Line ${index + 1} Q23 not applicable`} />
                    ) : (
                      <div className="grid grid-rows-2 gap-px bg-white">
                        <Input
                          className={cellInput}
                          data-grid-input="true"
                          inputMode="numeric"
                          placeholder="Pregnancies"
                          {...register(`members.${index}.livingChildren`)}
                        />
                        {q23TotalIsZero ? (
                          <SkippedInput ariaLabel={`Line ${index + 1} Q23 living children not applicable`} />
                        ) : (
                          <Input
                            className={cellInput}
                            data-grid-input="true"
                            inputMode="numeric"
                            placeholder="Living"
                            {...register(`members.${index}.livingChildrenAlive`)}
                          />
                        )}
                      </div>
                    )}
                  </DataCell>
                  <DataCell error={memberErrors?.familyPlanningUse?.message}>
                    {q24Skipped ? (
                      <SkippedInput ariaLabel={`Line ${index + 1} Q24 not applicable`} />
                    ) : (
                      <CodedInput
                        className={cellInput}
                        data-grid-input="true"
                        inputMode="numeric"
                        placeholder="0"
                        questionKey="q24_family_planning_use"
                        registration={register(`members.${index}.familyPlanningUse`)}
                        value={member?.familyPlanningUse}
                      />
                    )}
                  </DataCell>
                  <DataCell error={memberErrors?.fpMethodSource?.message ?? memberErrors?.fpMethodSourceOther?.message}>
                    {q25Skipped ? (
                      <SkippedInput ariaLabel={`Line ${index + 1} Q25 not applicable`} />
                    ) : (
                      <>
                        <CodedInput
                          className={cellInput}
                          data-grid-input="true"
                          inputMode="numeric"
                          placeholder="1"
                          questionKey="q25_fp_method_source"
                          registration={register(`members.${index}.fpMethodSource`)}
                          value={member?.fpMethodSource}
                        />
                        {q25Other ? (
                          <Input
                            autoUppercase
                            className={cn(cellInput, "border-t border-slate-300", memberErrors?.fpMethodSourceOther?.message && "bg-red-50")}
                            data-grid-input="true"
                            placeholder="Specify"
                            {...register(`members.${index}.fpMethodSourceOther`)}
                          />
                        ) : null}
                      </>
                    )}
                  </DataCell>
                  <DataCell error={memberErrors?.intentionToUseFp?.message ?? memberErrors?.intentionToUseFpDetail?.message}>
                    {q26Skipped ? (
                      <SkippedInput ariaLabel={`Line ${index + 1} Q26 not applicable`} />
                    ) : (
                      <>
                        <CodedInput
                          className={cellInput}
                          data-grid-input="true"
                          inputMode="numeric"
                          placeholder="1"
                          questionKey="q26_intention_to_use_fp"
                          registration={q26Registration}
                          value={member?.intentionToUseFp}
                        />
                        {q26Detail === "yes" ? (
                          <CodedInput
                            className={cn(cellInput, "border-t border-slate-300", memberErrors?.intentionToUseFpDetail?.message && "bg-red-50")}
                            data-grid-input="true"
                            inputMode="numeric"
                            placeholder="FP method"
                            questionKey="q26_intended_fp_method"
                            registration={register(`members.${index}.intentionToUseFpDetail`)}
                            value={member?.intentionToUseFpDetail}
                          />
                        ) : q26Detail === "no" ? (
                          <Input
                            autoUppercase
                            className={cn(cellInput, "border-t border-slate-300", memberErrors?.intentionToUseFpDetail?.message && "bg-red-50")}
                            data-grid-input="true"
                            placeholder="Reason"
                            {...register(`members.${index}.intentionToUseFpDetail`)}
                          />
                        ) : null}
                      </>
                    )}
                  </DataCell>
                </tr>
              );
            })}
          </tbody>
        </table>
      </ScrollableFormShell>
    </div>
  );
}

function QuestionHeader({
  code,
  title,
  children,
  className,
}: {
  code: string;
  title: string;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <th className={cn("border border-black px-2 py-3 align-middle font-medium uppercase", className)}>
      <span className="block font-bold">{code}</span>
      <span className="block">{title}</span>
      {children && <span className="mt-1 block text-[11px] font-normal normal-case leading-tight">{children}</span>}
    </th>
  );
}

function Instruction({ children }: { children?: ReactNode }) {
  return <th className="border border-black px-2 py-3 align-top text-[11px] font-normal leading-tight">{children}</th>;
}

function LineCell({ children }: { children: ReactNode }) {
  return <td className="border border-black bg-white px-2 py-1 align-middle font-semibold">{children}</td>;
}

function DataCell({ children, error }: { children: ReactNode; error?: string }) {
  return (
    <td className={cn("border border-black bg-white p-1 align-middle", error && "bg-red-50 ring-1 ring-inset ring-red-500")}>
      {children}
      {error ? <p className="px-1 pb-1 text-left text-[10px] font-semibold text-red-700">{String(error)}</p> : null}
    </td>
  );
}

function SkippedInput({ ariaLabel }: { ariaLabel: string }) {
  return (
    <Input
      aria-label={ariaLabel}
      className={cn(cellInput, "text-center font-semibold text-slate-600")}
      readOnly
      tabIndex={-1}
      value={notApplicableValue}
    />
  );
}

function MonthlyIncomeInput({
  registration,
}: {
  registration: ReturnType<UseFormRegister<AddNewDataFormValues>>;
}) {
  return (
    <Input
      className={cellInput}
      data-grid-input="true"
      inputMode="numeric"
      name={registration.name}
      onBlur={(event) => {
        const formattedValue = formatIncomeValue(event.currentTarget.value);
        event.currentTarget.value = formattedValue;
        void registration.onChange(event);
        registration.onBlur(event);
      }}
      onChange={(event) => {
        registration.onChange(event);
      }}
      placeholder="PHP"
      ref={registration.ref}
    />
  );
}

function formatIncomeValue(value: string) {
  const trimmedValue = value.trim();

  if (/^(NONE|NO INCOME|N\/A|NA)$/i.test(trimmedValue)) {
    return "0";
  }

  const numericValue = trimmedValue.replace(/,/g, "");

  if (!numericValue) {
    return "";
  }

  if (!/^\d+(?:\.\d+)?$/.test(numericValue)) {
    return trimmedValue;
  }

  return Number(numericValue).toLocaleString("en-US");
}

function isSkipped(member: AddNewDataFormValues["members"][number], field: MemberFieldName) {
  return getApplicabilityForQuestion(member, field) === "notApplicable";
}

function shouldShowDetail(
  value: string | undefined,
  questionKey: RbimCodeQuestionKey,
  code: string,
) {
  const result = cleanRBIMCode(questionKey, value);

  return !result.error && result.code === code && !hasInlineDetail(value, code);
}

function shouldShowFpIntentionDetail(value: string | undefined) {
  const result = cleanRBIMCode("q26_intention_to_use_fp", value);

  if (result.error || !["1", "2"].includes(result.code) || hasInlineDetail(value, result.code)) {
    return null;
  }

  return result.code === "1" ? "yes" : "no";
}

function hasInlineDetail(value: string | undefined, code: string) {
  return new RegExp(`^0*${code}(?:\\s*(?:-|:|,)\\s*|\\s+)\\S+`, "i").test(String(value ?? "").trim());
}

function isZeroEntry(value: string | undefined) {
  const normalizedValue = String(value ?? "").replace(/,/g, "").trim();

  return normalizedValue.length > 0 && Number(normalizedValue) === 0;
}
